import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import { eventService } from '../../services/eventService';
import './EventCalendar.css';

const EventCalendar = () => {
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [imageErrors, setImageErrors] = useState({});

  const getFullImageUrl = (imageUrl) => {
    if (!imageUrl) return null;
    if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
      return imageUrl;
    }
    if (imageUrl.startsWith('/nlnapi/')) {
      return `https://nlngraclub.com${imageUrl}`;
    }
    if (imageUrl.startsWith('/uploads/')) {
      return `https://nlngraclub.com${imageUrl}`;
    }
    if (imageUrl.startsWith('/')) {
      return `https://nlngraclub.com${imageUrl}`;
    }
    return `https://nlngraclub.com/${imageUrl}`;
  };

  const handleImageError = (eventId) => {
    setImageErrors(prev => ({ ...prev, [eventId]: true }));
  };

  const formatEventForCalendar = (apiEvent) => {
    // Combine date and time for start
    let startDateTime = apiEvent.event_date;
    if (apiEvent.event_time && apiEvent.event_time !== 'TBA') {
      startDateTime = `${apiEvent.event_date}T${apiEvent.event_time}`;
    } else {
      startDateTime = `${apiEvent.event_date}T00:00:00`;
    }
    
    // Calculate end time (default to 2 hours after start if not specified)
    let endDateTime = startDateTime;
    if (apiEvent.end_time) {
      endDateTime = `${apiEvent.event_date}T${apiEvent.end_time}`;
    } else {
      const startDate = new Date(startDateTime);
      startDate.setHours(startDate.getHours() + 2);
      endDateTime = startDate.toISOString();
    }
    
    return {
      id: apiEvent.id,
      title: apiEvent.title,
      start: startDateTime,
      end: endDateTime,
      extendedProps: {
        description: apiEvent.description,
        location: apiEvent.location,
        event_type: apiEvent.event_type,
        image_url: apiEvent.image_url,
        price: apiEvent.price || 'Free for members',
        capacity: apiEvent.max_attendees ? `${apiEvent.max_attendees} people` : 'Limited slots',
        status: apiEvent.status,
        event_time: apiEvent.event_time,
        end_time: apiEvent.end_time
      }
    };
  };

  useEffect(() => {
    const fetchEvents = async () => {
      setLoading(true);
      try {
        console.log('Fetching events for calendar...');
        const response = await eventService.getEvents(false, 100);
        console.log('Calendar API Response:', response);
        
        if (response && response.success && response.events && response.events.length > 0) {
          const formattedEvents = response.events.map(formatEventForCalendar);
          setEvents(formattedEvents);
        } else {
          console.log('No events found');
          setEvents([]);
        }
      } catch (error) {
        console.error('Error fetching events:', error);
        setEvents([]);
      } finally {
        setLoading(false);
      }
    };
    
    fetchEvents();
  }, []);

  const handleEventClick = (info) => {
    setSelectedEvent(info.event);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setTimeout(() => setSelectedEvent(null), 300);
  };

  // Handle escape key to close modal
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape' && isModalOpen) {
        closeModal();
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isModalOpen]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isModalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isModalOpen]);

  const formatEventDate = (dateString) => {
    if (!dateString) return 'Date TBA';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Date TBA';
      return date.toLocaleDateString('en-US', { 
        weekday: 'long', 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    } catch (e) {
      return 'Date TBA';
    }
  };

  const formatEventTime = (timeString) => {
    if (!timeString || timeString === 'TBA') return 'Time TBA';
    try {
      // If time is in HH:MM:SS format
      if (timeString.includes(':')) {
        const [hours, minutes] = timeString.split(':');
        const date = new Date();
        date.setHours(parseInt(hours), parseInt(minutes));
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      }
      return timeString;
    } catch (e) {
      return timeString;
    }
  };

  if (loading) {
    return (
      <div className="facility-detail-page">
        <section className="facility-detail-hero" style={{
          background: 'linear-gradient(135deg, #1e8a48 0%, #27a85a 50%, #4ade80 100%)',
          minHeight: '60vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative'
        }}>
          <div className="container">
            <div style={{ textAlign: 'center', padding: '60px 20px' }}>
              <i className="fas fa-spinner fa-spin" style={{ fontSize: '48px', color: 'white' }}></i>
              <p style={{ marginTop: '20px', color: 'white' }}>Loading calendar...</p>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="facility-detail-page">
      {/* Hero Section */}
      <section className="facility-detail-hero" style={{
        background: 'linear-gradient(135deg, #1e8a48 0%, #27a85a 50%, #4ade80 100%)',
        minHeight: '60vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative'
      }}>
        <div className="container">
          <motion.div 
            className="hero-content"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            style={{ textAlign: 'center', maxWidth: '800px', margin: '0 auto' }}
          >
            <Link to="/events" className="back-link" style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              color: 'rgba(255, 255, 255, 0.9)',
              textDecoration: 'none',
              fontSize: '0.875rem',
              fontWeight: '500',
              marginBottom: '20px',
              padding: '8px 16px',
              background: 'rgba(255, 255, 255, 0.2)',
              borderRadius: '40px',
              backdropFilter: 'blur(5px)',
              transition: 'all 0.3s ease'
            }}>
              <i className="fas fa-arrow-left"></i> Back to Events
            </Link>
            <span className="facility-category" style={{
              display: 'inline-block',
              fontSize: '0.75rem',
              fontWeight: '700',
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              color: 'rgba(255, 255, 255, 0.9)',
              background: 'rgba(255, 255, 255, 0.2)',
              padding: '6px 16px',
              borderRadius: '40px',
              marginBottom: '1.5rem'
            }}>
              Plan Your Month
            </span>
            <h1 className="facility-title" style={{
              fontFamily: "'Maven Pro', Georgia, serif",
              fontSize: 'clamp(2.5rem, 8vw, 4rem)',
              fontWeight: '800',
              color: 'white',
              marginBottom: '1rem',
              letterSpacing: '-0.02em'
            }}>
              Event Calendar
            </h1>
            <p className="facility-subtitle" style={{
              fontSize: 'clamp(1rem, 3vw, 1.25rem)',
              color: 'rgba(255, 255, 255, 0.9)',
              maxWidth: '600px',
              margin: '0 auto',
              lineHeight: '1.6'
            }}>
              View all club events, activities, and important dates
            </p>
            
            {/* Quick Stats */}
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              gap: '2rem',
              marginTop: '2rem',
              flexWrap: 'wrap'
            }}>
              <div style={{
                textAlign: 'center',
                padding: '0.75rem 1.5rem',
                background: 'rgba(255, 255, 255, 0.15)',
                borderRadius: '40px',
                backdropFilter: 'blur(5px)',
                minWidth: '100px'
              }}>
                <div style={{ fontSize: '1.5rem', fontWeight: '800', color: 'white', lineHeight: '1' }}>{events.length}</div>
                <div style={{ fontSize: '0.7rem', fontWeight: '600', color: 'rgba(255, 255, 255, 0.9)', textTransform: 'uppercase' }}>Upcoming Events</div>
              </div>
              <div style={{
                textAlign: 'center',
                padding: '0.75rem 1.5rem',
                background: 'rgba(255, 255, 255, 0.15)',
                borderRadius: '40px',
                backdropFilter: 'blur(5px)',
                minWidth: '100px'
              }}>
                <div style={{ fontSize: '1.5rem', fontWeight: '800', color: 'white', lineHeight: '1' }}>5+</div>
                <div style={{ fontSize: '0.7rem', fontWeight: '600', color: 'rgba(255, 255, 255, 0.9)', textTransform: 'uppercase' }}>Categories</div>
              </div>
              <div style={{
                textAlign: 'center',
                padding: '0.75rem 1.5rem',
                background: 'rgba(255, 255, 255, 0.15)',
                borderRadius: '40px',
                backdropFilter: 'blur(5px)',
                minWidth: '100px'
              }}>
                <div style={{ fontSize: '1.5rem', fontWeight: '800', color: 'white', lineHeight: '1' }}>50+</div>
                <div style={{ fontSize: '0.7rem', fontWeight: '600', color: 'rgba(255, 255, 255, 0.9)', textTransform: 'uppercase' }}>Annual Events</div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Calendar Content */}
      <section className="facility-content-section">
        <div className="container">
          {events.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 20px' }}>
              <i className="fas fa-calendar-times" style={{ fontSize: '48px', color: '#999', marginBottom: '20px' }}></i>
              <p>No events scheduled at this time.</p>
              <p style={{ fontSize: '14px', color: '#666' }}>Check back soon for upcoming events!</p>
            </div>
          ) : (
            <div className="facility-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem' }}>
              {/* Left Column - Calendar */}
              <div className="facility-gallery" style={{ width: '100%' }}>
                <div className="calendar-container">
                  <FullCalendar
                    plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
                    initialView="dayGridMonth"
                    headerToolbar={{
                      left: 'prev,next today',
                      center: 'title',
                      right: 'dayGridMonth,timeGridWeek,timeGridDay'
                    }}
                    events={events}
                    eventClick={handleEventClick}
                    height="auto"
                    aspectRatio={1.5}
                    eventClassNames="calendar-event"
                    slotMinTime="08:00:00"
                    slotMaxTime="22:00:00"
                    allDaySlot={true}
                    nowIndicator={true}
                    weekNumbers={true}
                    businessHours={{
                      daysOfWeek: [1, 2, 3, 4, 5],
                      startTime: '08:00',
                      endTime: '20:00',
                    }}
                  />
                </div>
              </div>

              {/* Right Column - Event Details */}
              <div className="facility-details">
                <div className="detail-section">
                  <h2>Upcoming Events</h2>
                  <p className="facility-description">
                    Browse through our calendar to find exciting events happening at the club.
                    Click on any event to see more details.
                  </p>
                </div>

                <div className="detail-section">
                  <h3>Featured Events</h3>
                  <ul className="features-list" style={{ listStyle: 'none', padding: 0 }}>
                    {events.slice(0, 4).map((event, index) => (
                      <li 
                        key={index} 
                        onClick={() => {
                          setSelectedEvent(event);
                          setIsModalOpen(true);
                        }} 
                        style={{ 
                          cursor: 'pointer', 
                          padding: '12px',
                          marginBottom: '8px',
                          background: '#f9fafb',
                          borderRadius: '8px',
                          transition: 'all 0.3s ease',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = '#e8f5e9'}
                        onMouseLeave={(e) => e.currentTarget.style.background = '#f9fafb'}
                      >
                        <i className="fas fa-calendar-alt" style={{ color: '#27a85a', fontSize: '1.2rem' }}></i>
                        <span style={{ flex: 1 }}>
                          <strong style={{ display: 'block', color: '#111827' }}>{event.title}</strong>
                          <small style={{ color: '#6b7280' }}>{formatEventDate(event.start)}</small>
                        </span>
                        <i className="fas fa-chevron-right" style={{ color: '#9ca3af', fontSize: '0.8rem' }}></i>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="info-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', marginTop: '1.5rem' }}>
                  <div className="info-card" style={{ textAlign: 'center', padding: '1rem', background: '#f9fafb', borderRadius: '12px' }}>
                    <i className="fas fa-calendar-week" style={{ fontSize: '1.5rem', color: '#27a85a', marginBottom: '0.5rem' }}></i>
                    <h4 style={{ fontSize: '0.8rem', marginBottom: '0.25rem' }}>Total Events</h4>
                    <p style={{ fontSize: '1.2rem', fontWeight: 'bold', color: '#27a85a' }}>{events.length}</p>
                  </div>
                  <div className="info-card" style={{ textAlign: 'center', padding: '1rem', background: '#f9fafb', borderRadius: '12px' }}>
                    <i className="fas fa-users" style={{ fontSize: '1.5rem', color: '#27a85a', marginBottom: '0.5rem' }}></i>
                    <h4 style={{ fontSize: '0.8rem', marginBottom: '0.25rem' }}>Monthly Events</h4>
                    <p style={{ fontSize: '1.2rem', fontWeight: 'bold', color: '#27a85a' }}>50+</p>
                  </div>
                  <div className="info-card" style={{ textAlign: 'center', padding: '1rem', background: '#f9fafb', borderRadius: '12px' }}>
                    <i className="fas fa-clock" style={{ fontSize: '1.5rem', color: '#27a85a', marginBottom: '0.5rem' }}></i>
                    <h4 style={{ fontSize: '0.8rem', marginBottom: '0.25rem' }}>Club Hours</h4>
                    <p style={{ fontSize: '1.2rem', fontWeight: 'bold', color: '#27a85a' }}>24/7</p>
                  </div>
                </div>

                <div className="additional-info" style={{ marginTop: '1.5rem', padding: '1rem', background: '#f9fafb', borderRadius: '12px' }}>
                  <div className="info-row" style={{ marginBottom: '0.5rem' }}>
                    <span className="info-label" style={{ fontWeight: 'bold' }}>Event Types:</span>
                    <span className="info-value"> Sports • Social • Family • Wellness • Parties</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label" style={{ fontWeight: 'bold' }}>Contact:</span>
                    <span className="info-value"> events@nlngraclub.com</span>
                  </div>
                </div>

                <div className="action-buttons" style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem' }}>
                  <Link to="/events" className="btn btn-primary btn-large" style={{ flex: 1, textAlign: 'center', padding: '12px', background: '#27a85a', color: 'white', borderRadius: '8px', textDecoration: 'none' }}>
                    <i className="fas fa-calendar-plus"></i> View All Events
                  </Link>
                  <Link to="/contact" className="btn btn-outline btn-large" style={{ flex: 1, textAlign: 'center', padding: '12px', border: '1px solid #27a85a', color: '#27a85a', borderRadius: '8px', textDecoration: 'none' }}>
                    <i className="fas fa-envelope"></i> Plan an Event
                  </Link>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Event Detail Modal - Enhanced with animation */}
      <AnimatePresence>
        {isModalOpen && selectedEvent && (
          <motion.div 
            className="modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeModal}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.7)',
              backdropFilter: 'blur(8px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000,
              padding: '1rem'
            }}
          >
            <motion.div 
              className="modal-content"
              initial={{ scale: 0.8, opacity: 0, y: 50 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.8, opacity: 0, y: 50 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              onClick={(e) => e.stopPropagation()}
              style={{
                background: 'white',
                borderRadius: '20px',
                maxWidth: '550px',
                width: '90%',
                maxHeight: '90vh',
                overflow: 'auto',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                position: 'relative'
              }}
            >
              {/* Close Button */}
              <button 
                onClick={closeModal} 
                style={{
                  position: 'absolute',
                  top: '1rem',
                  right: '1rem',
                  background: 'rgba(0, 0, 0, 0.5)',
                  border: 'none',
                  width: '36px',
                  height: '36px',
                  borderRadius: '50%',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  zIndex: 10,
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#27a85a'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(0, 0, 0, 0.5)'}
              >
                <i className="fas fa-times"></i>
              </button>

              {/* Modal Content */}
              <div style={{ padding: '2rem' }}>
                {/* Event Type Badge */}
                <div style={{ marginBottom: '1rem' }}>
                  <span style={{
                    display: 'inline-block',
                    background: '#e8f5e9',
                    color: '#27a85a',
                    padding: '4px 12px',
                    borderRadius: '20px',
                    fontSize: '0.75rem',
                    fontWeight: '600'
                  }}>
                    {selectedEvent.extendedProps.event_type || 'Event'}
                  </span>
                </div>

                {/* Event Title */}
                <h3 style={{
                  fontFamily: "'Maven Pro', Georgia, serif",
                  fontSize: 'clamp(1.5rem, 5vw, 2rem)',
                  fontWeight: '700',
                  color: '#111827',
                  marginBottom: '1rem'
                }}>
                  {selectedEvent.title}
                </h3>

                {/* Event Details */}
                <div style={{ marginBottom: '1.5rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
                    <i className="fas fa-calendar-alt" style={{ color: '#27a85a', width: '20px' }}></i>
                    <span style={{ color: '#4b5563' }}>{formatEventDate(selectedEvent.start)}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
                    <i className="fas fa-clock" style={{ color: '#27a85a', width: '20px' }}></i>
                    <span style={{ color: '#4b5563' }}>
                      {formatEventTime(selectedEvent.extendedProps.event_time) || 'Time TBA'}
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
                    <i className="fas fa-map-marker-alt" style={{ color: '#27a85a', width: '20px' }}></i>
                    <span style={{ color: '#4b5563' }}>{selectedEvent.extendedProps.location || 'Location TBA'}</span>
                  </div>
                  {selectedEvent.extendedProps.capacity && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
                      <i className="fas fa-users" style={{ color: '#27a85a', width: '20px' }}></i>
                      <span style={{ color: '#4b5563' }}>Capacity: {selectedEvent.extendedProps.capacity}</span>
                    </div>
                  )}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
                    <i className="fas fa-tag" style={{ color: '#27a85a', width: '20px' }}></i>
                    <span style={{ color: '#4b5563', fontWeight: '600' }}>{selectedEvent.extendedProps.price || 'Free for members'}</span>
                  </div>
                </div>

                {/* Description */}
                <div style={{ 
                  marginBottom: '1.5rem', 
                  padding: '1rem', 
                  background: '#f9fafb', 
                  borderRadius: '12px',
                  borderLeft: '3px solid #27a85a'
                }}>
                  <p style={{ color: '#4b5563', lineHeight: '1.6', margin: 0 }}>
                    {selectedEvent.extendedProps.description || 'No description available.'}
                  </p>
                </div>

                {/* Action Buttons */}
                <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                  <button 
                    onClick={closeModal}
                    style={{
                      flex: 1,
                      padding: '12px 24px',
                      background: '#27a85a',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontWeight: '600',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => e.target.style.background = '#1e8a48'}
                    onMouseLeave={(e) => e.target.style.background = '#27a85a'}
                  >
                    Close
                  </button>
                  <Link 
                    to="/booking"
                    style={{
                      flex: 1,
                      padding: '12px 24px',
                      background: 'white',
                      color: '#27a85a',
                      border: '1px solid #27a85a',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontWeight: '600',
                      textDecoration: 'none',
                      textAlign: 'center',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.background = '#e8f5e9';
                      e.target.style.transform = 'translateY(-2px)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = 'white';
                      e.target.style.transform = 'translateY(0)';
                    }}
                  >
                    Register Now
                  </Link>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default EventCalendar;